'use client';

import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Product } from 'shared/types/product/Product';
import ProductListCard from './ProductListCard';
import { useRouter } from 'next/navigation';
import { Button } from 'components/ui/button';
import ArrowPathIcon from '@heroicons/react/16/solid/ArrowPathIcon';
import { FacetFilter, Sort, SortOrderType } from 'shared/types/query/ProductQuery';
import ProductListFilters from './ProductListFilters';
import ProductListAppliedFilters from './ProductListAppliedFilters';
import ChevronDownIcon from 'icons/chevron-down';
import { RouteHelpers } from 'helpers/routerHelpers';
import ProductListSort, { SortAttributes } from './ProductListSort';
import Drawer from 'components/commercetools-ui/atoms/drawer';
import CloseIcon from 'icons/close';
import { FilterTypes } from 'shared/types/query/Filter';
import { ProductPaginatedResult } from 'shared/types/result/PaginatedResult';
import { gtmSelectItem, gtmViewItemList } from '../../../../../helpers/utils/gtm-events/gtmEccomerce';
import { ItemDataLayer, LineItemMapper } from '../../../../../helpers/utils/gtm-events/mapper/LineItemMapper';
import { observeElementsInViewport } from '../../../../../helpers/utils/viewportObserver';
import { gtmTrackEvent } from '../../../../../helpers/utils/gtm-events/gtmTrackEvent';
import ProductQuickViewModal from './ProductQuickViewModal';

interface ProductListProps {
  isLoading: boolean;
  productsPaginatedResults: { data: ProductPaginatedResult };
  activeFacets: FacetFilter[];
  setActiveFacets: React.Dispatch<React.SetStateAction<FacetFilter[]>>;
  offset: number;
  setOffset: React.Dispatch<React.SetStateAction<number>>;
  sort: Sort;
  setSort: (sort: Sort) => void;
  categoryId?: string;
  location?: string;
  isVisualisation?: boolean;
  defaultColor?: string;
}

const ProductList: React.FC<ProductListProps> = ({
  isLoading,
  productsPaginatedResults,
  activeFacets,
  setActiveFacets,
  offset,
  setOffset,
  sort,
  setSort,
  categoryId = '',
  location = '',
  isVisualisation = false,
  defaultColor,
}) => {
  const router = useRouter();
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isInitialSortSet, setIsInitialSortSet] = useState(false);

  const showMoreThreshold = productsPaginatedResults.data?.showMoreThreshold || 0;
  const products = productsPaginatedResults?.data?.items || [];
  const totalItems = productsPaginatedResults.data?.total || 0;
  const batchCount = productsPaginatedResults.data?.count || 0;
  const allFacets = productsPaginatedResults.data?.facets || [];
  const isDisplayMoreButton = offset > 0 && (offset + batchCount) % showMoreThreshold === 0;

  const handleScroll = useCallback(() => {
    const scrollPosition = window.scrollY + window.innerHeight;
    const bottomPosition = document.documentElement.scrollHeight;

    if (
      scrollPosition >= bottomPosition - 500 &&
      !isLoading &&
      !isDisplayMoreButton &&
      offset + batchCount < totalItems
    ) {
      setOffset(offset + batchCount);
    }
  }, [isLoading, isDisplayMoreButton, offset, batchCount, totalItems]);

  const onClickLoadMoreResults = () => {
    gtmTrackEvent('plp_interactions', { click_action: 'load more' });
    setOffset(offset + batchCount);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const handleFilterChange = useCallback((filter?: FacetFilter) => {
    const previousScroll = window.scrollY;
    document.body.style.overflow = 'hidden';

    if (filter) {
      setActiveFacets((prev: FacetFilter[]) => {
        const existingFilter = prev.find((f: FacetFilter) => f.key === filter.key);
        const newTerm = filter.facetData.terms?.[0];

        let newFilters: FacetFilter[];
        if (existingFilter) {
          const hasExistingTerm = existingFilter.facetData.terms?.includes(newTerm!);

          if (hasExistingTerm) {
            // Remove the term
            const updatedTerms = existingFilter.facetData.terms?.filter((term: string) => term !== newTerm);

            // If no terms left, remove the entire filter
            if (updatedTerms?.length === 0) {
              newFilters = prev.filter((f: FacetFilter) => f.key !== filter.key);
            } else {
              newFilters = prev.map((f: FacetFilter) =>
                f.key === filter.key ? { ...f, facetData: { ...f.facetData, terms: updatedTerms } } : f,
              );
            }
          } else {
            // Add the new term to existing terms
            newFilters = prev.map((f: FacetFilter) =>
              f.key === filter.key
                ? { ...f, facetData: { ...f.facetData, terms: [...(f.facetData.terms || []), newTerm!] } }
                : f,
            );
          }
        } else {
          newFilters = [...prev, filter];
        }

        return newFilters;
      });
    } else {
      setActiveFacets([]);
    }

    setOffset(0);

    requestAnimationFrame(() => {
      window.scrollTo({ top: previousScroll });
      document.body.style.overflow = '';
    });
  }, []);

  useEffect(() => {
    if (allFacets.length > 0 && !isVisualisation) {
      const url = RouteHelpers.generatePlpURL(activeFacets, sort);
      router.replace(url, { scroll: false });
    }
  }, [activeFacets, sort]);

  useEffect(() => {
    // The page loaded with URL query params, so we need to set the active facets
    if (typeof window !== 'undefined') {
      const urlParams = RouteHelpers.decodeToObj(window.location.search);

      if (!isInitialSortSet && urlParams?.sort && typeof urlParams.sort === 'string' && !isLoading) {
        const sortKey = Object.keys(SortOrderType).find((key) => key === (urlParams.sort as string).toUpperCase());

        if (sortKey) {
          const sortOrder = SortOrderType[sortKey as keyof typeof SortOrderType];
          const initialSort = SortAttributes.find((s) => s.orderType === sortOrder);

          if (initialSort) setSort(initialSort);
        }
      }
      if (!isLoading && !isInitialSortSet) setIsInitialSortSet(true);

      if (Object.keys(urlParams).length > 0 && activeFacets.length === 0 && allFacets?.length > 0 && !isLoading) {
        const initialFacets: FacetFilter[] = [];
        allFacets.map((facet) => {
          const facetKey = facet.distinct?.field?.split('.')?.pop();
          const urlFacet = urlParams[facetKey];

          if (urlFacet) {
            initialFacets.push({
              key: facet.distinct.field,
              urlKey: facetKey,
              facetData: {
                facetType: facet.distinct.fieldType as FilterTypes,
                terms: (Array.isArray(urlFacet) ? urlFacet : [urlFacet]) as string[],
              },
            });
          }
        });
        setActiveFacets(initialFacets as FacetFilter[]);
      }
    }
  }, [isLoading]);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewedProducts = useRef<Product[]>([]);
  const observedProducts = useRef(new Map());

  const dataLayerEventTrigger = () => {
    if (viewedProducts.current.length) {
      gtmViewItemList({
        item_list_id: categoryId,
        item_list_name: location,
        filter_applied: (activeFacets.length !== 0).toString(),
        selected_option: activeFacets
          .map((f) => f.facetData.terms)
          .flat()
          .toString(),
        filter_type: activeFacets
          .map(
            (f) => (productsPaginatedResults?.data?.facets ?? []).find((facet) => facet.distinct.field === f.key)?.name,
          )
          .toString(),
        itemsDataLayer: viewedProducts.current.map((product) =>
          LineItemMapper.fromProductToItemDataLayer(product, 1, product.masterVariant!),
        ),
      });
    }
  };

  const handleRef = (element: HTMLDivElement | null, product: Product) => {
    if (element) {
      if (!observedProducts.current.has(product.productKey)) {
        const observer = observeElementsInViewport({
          element: element,
          callback: () => {
            if (!viewedProducts.current.map((p) => p.productKey).includes(product.productKey)) {
              viewedProducts.current.push(product);
            }

            if (debounceTimer.current) {
              clearTimeout(debounceTimer.current);
            }

            debounceTimer.current = setTimeout(() => {
              dataLayerEventTrigger();
              viewedProducts.current = [];
            }, 500);
          },
        });
        observedProducts.current.set(product.productKey, { observer, element });
      }
    } else if (observedProducts.current.has(product.productKey)) {
      const { observer, element: elem } = observedProducts.current.get(product.productKey);
      observer.unobserve(elem);
      observer.disconnect();
      observedProducts.current.delete(product.productKey);
    }
  };

  const onDLSelectItemOveride = (d: object) => {
    gtmSelectItem({
      item_list_id: categoryId,
      item_list_name: location,
      filter_applied: (activeFacets.length !== 0).toString(),
      selected_option: activeFacets
        .map((f) => f.facetData.terms)
        .flat()
        .toString(),
      filter_type: activeFacets
        .map(
          (f) => (productsPaginatedResults?.data?.facets ?? []).find((facet) => facet.distinct.field === f.key)?.name,
        )
        .toString(),
      ...(d as { itemsDataLayer: ItemDataLayer }),
    });
  };

  return (
    <div className="pb-10" role="main" aria-labelledby="title">
      <Drawer
        isOpen={showMobileFilters}
        direction="left"
        className="w-full"
        onClose={() => setShowMobileFilters(false)}
        aria-label="Filter options"
      >
        <div className="flex h-full flex-row">
          <div className="flex w-[90vw] flex-col">
            <div className="bg-neutral-250 p-5 text-sm font-bold" data-testid="mobile-filters-title">
              Filters
            </div>
            <ProductListFilters facets={allFacets} activeFacets={activeFacets} onFilterChange={handleFilterChange} />
          </div>
          <div
            className="flex-grow bg-black/75"
            onClick={() => setShowMobileFilters(false)}
            role="button"
            aria-label="Close filters"
          >
            <CloseIcon
              className="absolute right-0 top-0 z-[700] mx-auto mt-6 flex w-[10vw] items-center justify-center"
              color="white"
            />
          </div>
        </div>
      </Drawer>
      <div className="sticky top-0 ml-auto flex flex-col items-start gap-3 lg:w-5/6 lg:flex-row">
        <div className="ml-auto flex w-full flex-row items-start lg:pl-10">
          <ProductListAppliedFilters
            activeFacets={activeFacets}
            onFilterClick={handleFilterChange}
            onClearAll={() => handleFilterChange()}
          />
        </div>
        <div className="sticky top-[200pcpx] mb-6 grid w-full grid-cols-2 flex-row gap-5 lg:flex lg:w-auto">
          <Button
            aria-label="Open filters"
            className="boder-[1px] flex flex-1 items-center justify-between rounded-md border border-gray-300 p-3 text-sm font-normal lg:hidden"
            onClick={() => setShowMobileFilters(true)}
          >
            Filters <ChevronDownIcon className="h-5 w-5 -rotate-90 fill-primary-gray" />
          </Button>

          <ProductListSort
            sort={sort}
            onSortChange={(sort) => {
              setSort(sort);
              setOffset(0);
            }}
          />
        </div>
      </div>
      {isLoading && products.length === 0 && (
        <div className="flex h-16 w-full items-center justify-center gap-x-2">
          <ArrowPathIcon className="h-4 w-4 animate-spin fill-primary-gray stroke-1" />
          <p>Loading...</p>
        </div>
      )}
      <div className="flex gap-10">
        {products.length > 0 && (
          <div className="hidden lg:block lg:w-1/6">
            <ProductListFilters facets={allFacets} activeFacets={activeFacets} onFilterChange={handleFilterChange} />
          </div>
        )}
        <div className="w-full lg:w-4/5">
          <ul className="grid min-h-[1000px] grid-cols-2 gap-4 sm:min-h-fit sm:gap-5 md:gap-[27px] lg:grid-cols-3">
            <ProductListContainer
              products={products}
              offset={offset}
              handleRef={handleRef}
              onDLSelectItemOveride={(d) => onDLSelectItemOveride(d)}
              defaultColor={defaultColor}
            />
          </ul>
          {isLoading && products.length > 0 && (
            <div className="flex h-16 w-full items-center justify-center gap-x-2">
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin fill-primary-gray stroke-1" />
                <p>Loading...</p>
              </>
            </div>
          )}

          {isDisplayMoreButton && (
            <div className="flex w-full items-center justify-center">
              <Button
                aria-label="Load More Results"
                onClick={onClickLoadMoreResults}
                className="mx-auto mt-4 h-10 rounded bg-[#404040] px-5 text-sm font-bold leading-[17px] text-white"
              >
                Load More Results
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface ProductListContainerProps {
  products: Product[];
  offset: number;
  handleRef: (a: HTMLDivElement | null, b: Product) => void;
  onDLSelectItemOveride?: (d: object) => void;
  defaultColor?: string;
}

const ProductListContainer: React.FC<ProductListContainerProps> = ({
  products,
  offset,
  handleRef,
  onDLSelectItemOveride,
  defaultColor,
}) => {
  const [productDisplay, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    if (offset === 0) {
      setProducts(products);
    } else {
      setProducts([...productDisplay, ...products]);
    }
  }, [products]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(undefined);

  const selectProduct = (product: Product) => {
    setSelectedProduct(product);
  };
  const openModal = () => {
    setIsOpen(true);
  };
  return (
    <>
      {selectedProduct && isOpen && (
        <ProductQuickViewModal isOpen={isOpen} onClose={() => setIsOpen(false)} product={selectedProduct} />
      )}
      {productDisplay.map((product, idx) => (
        <div key={`category-product-${product.productKey}-${idx}`} ref={(el) => handleRef(el, product)}>
          <ProductListCard
            product={product}
            onDLSelectItemOveride={onDLSelectItemOveride}
            defaultColor={defaultColor}
            selectProduct={selectProduct}
            openModal={openModal}
          />
        </div>
      ))}

      <script
        type="application/ld+json"
        id="product-list-schema"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: productDisplay?.map((product: Product, index: number) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'Product',
                name: product.name,
                description: product.description,
                image: product.variants?.[0]?.images?.[0],
                sku: product.variants?.[0]?.sku?.replace(/[^a-zA-Z0-9]/g, ''),
                brand: {
                  '@type': 'Brand',
                  name: 'Shades of Light',
                },
                ageGroup: 'adult',
                gender: 'unisex',
                offers: {
                  '@type': 'Offer',
                  itemCondition: 'https://schema.org/NewCondition',
                  url: `https://www.shadesoflight.com${product._url}`,
                  priceCurrency: product?.variants?.[0].price?.currencyCode,
                  price: (product?.variants[0]?.price?.centAmount || 0) / 100,
                  availability: 'https://schema.org/InStock',
                  ...(product.variants[0].discountedPrice && {
                    salePrice: (product?.variants?.[0]?.originalPrice?.centAmount || 0) / 100,
                  }),
                },
                ...(product?.variants?.[0].attributes?.actualColor && {
                  color: product?.variants?.[0].attributes.actualColor,
                }),
                ...(product?.variants?.[0].attributes?.actualSize && {
                  size: product?.variants?.[0].attributes.actualSize,
                  sizeSystem: 'US',
                }),
              },
            })),
            numberOfItems: productDisplay.length,
          }),
        }}
      />
    </>
  );
};
export default ProductList;
